import { initPlayer } from './player';

interface HsFormCallbackData {
  type: 'hsFormCallback';
  eventName: string;
  id?: string;
  data?: { formGuid?: string };
}

function isHsFormCallback(data: unknown): data is HsFormCallbackData {
  return (
    typeof data === 'object' &&
    data !== null &&
    (data as Record<string, unknown>).type === 'hsFormCallback'
  );
}

function initVideoDemo(block: HTMLElement): void {
  const videoBox = block.querySelector<HTMLElement>('.video-demo__video');
  const video = block.querySelector<HTMLVideoElement>('video.video-demo__media');
  const form = block.querySelector<HTMLElement>('.wp-block-interact-hubspot-form');
  const formId = form?.getAttribute('data-form-id') || block.id || '';
  const storageKey = formId ? `interact:video-demo-unlocked:${formId}` : '';

  const fullPoster = videoBox?.dataset.fullPoster || '';

  const unlock = (persist: boolean): void => {
    // Idempotent — the render filter may pre-set data-state to avoid a reload flash.
    block.dataset.state = 'unlocked';
    if (video) video.controls = true;
    if (video && fullPoster) video.setAttribute('poster', fullPoster);
    if (persist && storageKey) {
      try {
        localStorage.setItem(storageKey, '1');
      } catch {
        // Storage blocked (private mode / quota) — unlock still works for this view.
      }
    }
  };

  // `?viewnow` bypasses the gate and jumps straight to the video, matching the
  // on-demand webinar behaviour.
  const viewNow = new URLSearchParams(window.location.search).has('viewnow');

  if (viewNow) {
    unlock(false);
  } else if (storageKey) {
    try {
      if (localStorage.getItem(storageKey) === '1') unlock(false);
    } catch {
      // Ignore read failures.
    }
  }

  initPlayer(block, {
    prefix: 'video-demo',
    onLockedPlay: () => {
      // While gated, the play button nudges the visitor to the form instead of
      // exposing the video.
      const firstField = form?.querySelector<HTMLElement>('input, select, textarea');
      (firstField ?? form)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      firstField?.focus({ preventScroll: true });
    },
  });

  window.addEventListener('message', (event: MessageEvent) => {
    if (!isHsFormCallback(event.data) || event.data.eventName !== 'onFormSubmitted') return;
    const submittedId = event.data.id || event.data.data?.formGuid || '';
    // Scope to this block's form when both ids are known; otherwise unlock optimistically.
    if (formId && submittedId && submittedId !== formId) return;
    unlock(true);
    video?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
}

function init(): void {
  document.querySelectorAll<HTMLElement>('.wp-block-interact-video-demo').forEach(initVideoDemo);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

export {};
